<script setup lang="ts">
import type { SaferGamblingPaths } from '@content-utils/schemas'
import { computed, ref } from '#imports'

const props = withDefaults(defineProps<{
    backLabel?: string
}>(), {
    backLabel: 'Back to overview',
})

const { queryAll } = useLayeredContent()

const { data: allPaths } = useAsyncData('saferGambling:paths', () =>
    queryAll<SaferGamblingPaths>('safer-gambling/paths')
)

const entrancePaths = computed(() => {
    if (!allPaths.value) return []
    return [...allPaths.value]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
})
const view = ref('Main')

function goToView(value: string) {
    view.value = value
    if (value === 'Main') {
        window.scrollTo(0, 0)
    }
}

const currentPath = computed(() =>
    entrancePaths.value?.find(p => p.title === view.value)
)
</script>

<template>
    <div class="entrance">
        <div class="entrance__headings">
            <h4 v-if="$slots.subtitle" class="entrance__subtitle">
                <slot name="subtitle" />
            </h4>
            <h1 class="entrance__headline">
                <template v-if="view !== 'Main' && currentPath">
                    {{ currentPath.title }}
                </template>
                <slot v-else name="headline" />
            </h1>
        </div>

        <Transition name="fade" mode="out-in">
            <div v-if="view === 'Main'" key="main">
                <div class="entrance__wrapper">
                    <slot name="entrance-text" />
                    <div class="entrance__paths">
                        <div
                            v-for="(entrancePath, index) in entrancePaths"
                            :key="index"
                            class="entrance__path"
                            @click="goToView(entrancePath.title)"
                        >
                            <UIIcon
                                v-if="entrancePath.icon"
                                class="entrance__path-icon"
                                :name="entrancePath.icon as any"
                                size="50px"
                            />

                            <span class="entrance__path-text">{{ entrancePath.title }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <SaferGamblingEntrancePanel
                v-else-if="currentPath"
                :key="currentPath.category"
                :title="currentPath.title"
                :icon="currentPath.icon"
                :category="currentPath.category"
                :header="currentPath.header"
                :back-label="props.backLabel"
                @go-back="goToView('Main')"
            />
        </Transition>
    </div>
</template>

<style lang="scss" scoped>
.entrance {
    text-align: center;
    margin-bottom: 20px;
    position: relative;

    &__headings {
        margin-bottom: 10px;
    }

    &__subtitle {
        color: $brand-primary;
        font-weight: $text-bold;
        margin: 0;
    }

    &__headline {
        margin: 0;
    }

    .entrance__wrapper {
        padding: 15px;
        background-color: $panel-color;
        border-radius: 8px;
        position: relative;
    }

    &__paths {
        display: grid;
        grid-column-gap: 10px;
        grid-template-columns: 1fr 1fr;
        margin-top: 15px;
    }

    &__path {
        background-color: $safer-gambling-qa-bg-color;
        aspect-ratio: 4 / 3;
        border-radius: 4px;
        color: $link-color;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        @supports not (aspect-ratio: 4 / 3) {
            min-height: 105px;
        }

        @include mobile(sm) {
            aspect-ratio: 16 / 9;

            @supports not (aspect-ratio: 16 / 9) {
                min-height: 175px;
            }
        }

        @include desktop-xl {
            @supports not (aspect-ratio: 16 / 9) {
                min-height: 200px;
            }
        }

        &-icon {
            margin-bottom: 10px;
        }

        &-text {
            font-weight: $text-heavy;
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}
</style>
